import { useState, useRef, useCallback } from 'react'
import timeEntryService from '@api/v1/timeEntryService'
import BillingEntryCard from './BillingEntryCard'
import VoiceButton from './VoiceButton'

/**
 * BillingChat — conversational interface for creating time & expense entries.
 * Type or speak a request, AI parses it, presents a card for review/approval.
 */
export default function BillingChat({ onClose }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'How can I help? Describe a time or expense entry and I\'ll set it up for you.' },
  ])
  const [input, setInput] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }, 100)
  }, [])

  const handleSubmit = useCallback(async (text) => {
    const trimmed = (text || input).trim()
    if (!trimmed || isProcessing) return

    setInput('')
    setMessages((prev) => [...prev, { role: 'user', text: trimmed }])
    setIsProcessing(true)
    scrollToBottom()

    try {
      const parsed = await timeEntryService.parseEntry(trimmed)
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: parsed.matter_name
          ? `Got it — here's the ${parsed.entry_type || 'time'} entry for "${parsed.matter_name}". Review and approve:`
          : 'I parsed your request. Please review:',
        proposal: parsed,
      }])
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: `Sorry, I couldn't parse that. ${err.response?.data?.error || err.message || 'Please try again with more details.'}`,
      }])
    } finally {
      setIsProcessing(false)
      scrollToBottom()
    }
  }, [input, isProcessing, scrollToBottom])

  const handleVoiceTranscription = useCallback((text) => {
    setInput(text)
    // Auto-submit voice input
    handleSubmit(text)
  }, [handleSubmit])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-cb-accent-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-900">Quick Entry</h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded text-gray-500 hover:text-gray-600 hover:bg-gray-100">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${msg.role === 'user' ? '' : ''}`}>
              <div className={`rounded-xl px-3.5 py-2.5 text-sm ${
                msg.role === 'user'
                  ? 'bg-cb-accent text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {msg.text}
              </div>
              {msg.proposal && (
                <div className="mt-2">
                  <BillingEntryCard proposal={msg.proposal} />
                </div>
              )}
            </div>
          </div>
        ))}

        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-500">Parsing your request...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 px-3 py-3">
        <div className="flex items-end gap-2">
          <VoiceButton onTranscription={handleVoiceTranscription} disabled={isProcessing} />
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='e.g. "Log 2 hours on Smith matter for contract review"'
              rows={1}
              className="w-full resize-none text-sm border border-gray-200 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-cb-accent/30 focus:border-cb-accent placeholder:text-gray-500"
            />
            <button
              onClick={() => handleSubmit()}
              disabled={!input.trim() || isProcessing}
              className="absolute right-2 bottom-2 p-1 rounded text-cb-accent-text hover:bg-cb-accent/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          </div>
        </div>
        <p className="text-[10px] text-gray-500 mt-1.5 px-1">
          Describe a time or expense entry in natural language. Use the mic for voice input.
        </p>
      </div>
    </div>
  )
}
